<template>
  <!-- An automatic value: the grade, the two ranges in euros, the trusted comparables value
       and, for D, the wide-range warning. The price itself is the page's headline. -->
  <div v-if="gates.auto_value" data-testid="qv-gates" class="space-y-2">
    <div class="flex items-center justify-center gap-2 text-sm text-slate-500">
      <span>{{ $t('messages.quickValuation.gatesV1.gradeLabel') }}</span>
      <Tag
        data-testid="qv-gates-grade"
        :value="gates.grade"
        :severity="gradeSeverity"
        class="text-xs!"
      />
      <span>{{ gradeWord }}</span>
    </div>
    <p v-if="range68" data-testid="qv-gates-range68" class="text-sm text-slate-700 tabular-nums">
      {{ range68 }}
    </p>
    <p v-if="range90" data-testid="qv-gates-range90" class="text-xs text-slate-500 tabular-nums">
      {{ range90 }}
    </p>
    <p v-if="range68" class="text-xs text-slate-400 max-w-md mx-auto">
      {{ $t('messages.quickValuation.gatesV1.rangeHint') }}
    </p>
    <p v-if="gates.second_value" data-testid="qv-gates-second-value" class="text-sm text-slate-600 tabular-nums">
      {{ $t('messages.quickValuation.gatesV1.secondValue', { value: euros(gates.second_value) }) }}
    </p>
    <Message
      v-if="gates.warning === 'wide_range'"
      data-testid="qv-gates-wide-range"
      severity="warn"
      :closable="false"
      class="text-left"
    >
      {{ $t('messages.quickValuation.gatesV1.wideRange') }}
    </Message>
  </div>

  <!-- No automatic value (grade `valuer`, or the area is out of scope): no number at all. -->
  <div v-else data-testid="qv-gates-no-value" class="py-2">
    <span class="pi pi-user text-2xl text-slate-400 mb-3 block"></span>
    <p class="text-lg text-slate-700">
      {{ $t('messages.quickValuation.gatesV1.noValueTitle') }}
    </p>
    <p class="text-sm text-slate-500 mt-2 max-w-md mx-auto">
      {{ gates.status === 'out_of_scope'
        ? $t('messages.quickValuation.gatesV1.outOfScope')
        : $t('messages.quickValuation.gatesV1.valuer') }}
    </p>
  </div>
</template>

<script setup>
/**
 * RED-262 gates v1 (Jaan's GATES_SPEC_DEV_v1, DEV, flag `gates-v1`): what the price card shows
 * under a `prediction.gates` block that decided the price (`ok` or `out_of_scope`).
 *
 *  - Grades A–D: the headline is the locked-scale model's price (the page prints
 *    `display_price`); this adds the grade, the 68 % range in euros with its ±%, the 90 % range
 *    as a quieter second line, one plain sentence on what the two ranges mean, the comparables
 *    value only when the server sent `second_value` (grade A/B and comps grade A), and the
 *    "wide range" warning for D.
 *  - `valuer` / `out_of_scope`: no automatic value, a note that a price needs a valuer's
 *    assessment (a state, not a promise: nothing assigns a valuer). The page hides every
 *    route to a number with it: the kohandustabel link, comparables, factors, model quality.
 *
 * DRAFT wording: Toomas approves it before anything goes live.
 */
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  // The persisted `prediction.gates` block (App\Services\Valuation\GatesV1::normalize).
  gates: { type: Object, required: true },
})

const { t, te } = useI18n()

const euroFormat = new Intl.NumberFormat('et-EE', { maximumFractionDigits: 0 })
const pctFormat = new Intl.NumberFormat('et-EE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })

const euros = value => euroFormat.format(Math.round(value))

const gradeSeverity = computed(() => ({
  A: 'success',
  B: 'success',
  C: 'info',
  D: 'warn',
}[props.gates.grade] ?? 'secondary'))

const gradeWord = computed(() => {
  const key = `messages.quickValuation.gatesV1.grade.${props.gates.grade}`
  return te(key) ? t(key) : ''
})

// Range keys arrive as "68" / "90" (JSON object keys).
const band = level => {
  const b = props.gates.range?.[level]
  return b && b.lo != null && b.hi != null ? b : null
}

const range68 = computed(() => {
  const b = band('68')
  if (!b) return null
  const amounts = { lo: euros(b.lo), hi: euros(b.hi) }
  return b.pct != null
    ? t('messages.quickValuation.gatesV1.range68', { ...amounts, pct: pctFormat.format(b.pct) })
    : t('messages.quickValuation.gatesV1.range68NoPct', amounts)
})

const range90 = computed(() => {
  const b = band('90')
  return b ? t('messages.quickValuation.gatesV1.range90', { lo: euros(b.lo), hi: euros(b.hi) }) : null
})
</script>
